import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { uuid } from '@softline/core';
import { Subscription } from 'rxjs';
import { AiAgentRepository } from '../repositories/ai-agent.repository';
import {
  AgUiAssistantMessage,
  AgUiContext,
  AgUiEvent,
  AgUiInputMessage,
  RunAgentInput,
} from '../types/ag-ui.types';
import { ChatMention } from '../types/chat-mention.type';
import { ChatMessage, ChatToolCall, RunState } from '../types/chat-message.type';
import {
  failRun,
  pendingClientToolCalls,
  reduceAgUiEvent,
  setFollowups,
  setToolCallStatus,
} from './ag-ui-reducer';
import { AiAgentClientToolRegistry } from './ai-agent-client-tool.registry';

/** Client tool → continuation run → client tool … per user turn. */
export const MAX_CLIENT_TOOL_HOPS = 5;

export interface SendOptions {
  mentions?: ChatMention[];
  context?: AgUiContext[];
}

/** What one `agent/run` needs to know about the turn it belongs to. */
interface RunContext {
  runId: string;
  context: AgUiContext[];
  hop: number;
}

@Injectable()
export class AiAgentChatService {
  private readonly repository = inject(AiAgentRepository);
  private readonly injector = inject(Injector);
  private readonly clientTools = inject(AiAgentClientToolRegistry);

  private readonly state = signal<RunState>({
    messages: [],
    running: false,
    hop: 0,
  });
  private subscription?: Subscription;
  /** Identifies the run a `complete` callback belongs to; a continuation bumps it. */
  private runToken = 0;
  private executingClientTools = false;

  /** Owned by the client and stable for the conversation; the server keys memory on it. */
  readonly threadId = signal(`sl_agent_${uuid()}`);

  readonly messages = computed(() => this.state().messages);
  readonly isRunning = computed(() => this.state().running);
  readonly error = computed(() => this.state().error);
  readonly canSend = computed(() => !this.state().running);

  send(text: string, options?: SendOptions): void {
    const message = text.trim();
    if (message.length === 0 || this.state().running) return;

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      text: message,
      streaming: false,
      toolCalls: [],
      mentions: options?.mentions?.length ? options.mentions : undefined,
    };

    this.state.update((s) => ({
      ...s,
      messages: [...s.messages, userMessage],
      running: true,
      error: undefined,
      hop: 0,
    }));

    // Only the current turn goes on the wire — server memory supplies the thread.
    this.startRun(
      [{ id: userMessage.id, role: 'user', content: message }],
      options?.context ?? [],
      0
    );
  }

  /** Attaches suggested next prompts to the newest assistant message. */
  setFollowups(prompts: string[]): void {
    this.state.update((s) => setFollowups(s, prompts));
  }

  /** Unsubscribing aborts the HTTP request, which unsubscribes the run server-side. */
  abort(): void {
    this.runToken++;
    this.executingClientTools = false;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    if (this.state().running) this.state.update((s) => failRun(s, 'aborted'));
  }

  reset(): void {
    this.abort();
    this.threadId.set(`sl_agent_${uuid()}`);
    this.state.set({ messages: [], running: false, hop: 0 });
  }

  async rate(messageId: string, value: number): Promise<void> {
    const message = this.state().messages.find((m) => m.id === messageId);
    if (!message?.traceId) return;
    try {
      await this.repository.sendFeedback({ traceId: message.traceId, value });
      this.state.update((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.id === messageId ? { ...m, feedback: value } : m
        ),
      }));
    } catch (e) {
      // 503 while Langfuse is unconfigured is expected locally, not a crash.
      handleRequestError(e, this.injector, { show: true });
    }
  }

  private startRun(
    messages: AgUiInputMessage[],
    context: AgUiContext[],
    hop: number
  ): void {
    const input: RunAgentInput = {
      threadId: this.threadId(),
      runId: uuid(),
      messages,
      tools: this.clientTools.toWire(),
      context,
      state: {},
      forwardedProps: {},
    };
    const run: RunContext = { runId: input.runId, context, hop };
    const token = ++this.runToken;

    this.state.update((s) => ({ ...s, running: true, hop }));

    this.subscription?.unsubscribe();
    this.subscription = this.repository.run(input).subscribe({
      next: (event) => this.onEvent(event, run),
      error: (e) => {
        this.state.update((s) => failRun(s, String((e as Error)?.message ?? e)));
        handleRequestError(e, this.injector, { show: true });
      },
      complete: () => {
        // A continuation run has already replaced this subscription; its own
        // complete handler owns the outcome.
        if (token !== this.runToken || this.executingClientTools) return;
        if (this.state().running)
          this.state.update((s) => failRun(s, 'stream ended without RUN_FINISHED'));
      },
    });
  }

  private onEvent(event: AgUiEvent | ErrorEvent, run: RunContext): void {
    if (event instanceof ErrorEvent) {
      this.state.update((s) => failRun(s, event.message || 'connection failed'));
      return;
    }
    this.state.update((s) => reduceAgUiEvent(s, event));
    if (event.type === 'RUN_FINISHED') this.onRunFinished(run);
  }

  /**
   * A client tool ends the run without a result, so the browser executes it and
   * starts a continuation run carrying the assistant message and the result.
   */
  private onRunFinished(run: RunContext): void {
    const pending = pendingClientToolCalls(
      this.state(),
      run.runId,
      this.clientTools.names()
    );
    if (pending.length === 0) return;

    if (run.hop + 1 > MAX_CLIENT_TOOL_HOPS) {
      this.state.update((s) => failRun(s, '#AI_AGENT.HOP_LIMIT'));
      return;
    }

    this.executingClientTools = true;
    this.state.update((s) => {
      let next: RunState = { ...s, running: true };
      for (const call of pending) next = setToolCallStatus(next, call.id, 'executing');
      return next;
    });
    void this.executeClientTools(pending, run);
  }

  private async executeClientTools(
    pending: ChatToolCall[],
    run: RunContext
  ): Promise<void> {
    const messages: AgUiInputMessage[] = [];

    // Grouped by the assistant message that carried them: Mastra dedupes that
    // message by id, so the group has to be replayed as one.
    for (const [parentMessageId, calls] of groupByParent(pending)) {
      const assistant: AgUiAssistantMessage = {
        id: parentMessageId ?? uuid(),
        role: 'assistant',
        toolCalls: calls.map((c) => ({
          id: c.id,
          type: 'function',
          function: { name: c.name, arguments: c.arguments || '{}' },
        })),
      };
      messages.push(assistant);
      for (const call of calls) {
        const outcome = await this.executeClientTool(call);
        messages.push({
          id: uuid(),
          role: 'tool',
          toolCallId: call.id,
          content: outcome.content,
          ...(outcome.error ? { error: outcome.error } : {}),
        });
      }
    }

    this.executingClientTools = false;

    // A round of nothing but silent tools ends the turn. Re-prompting the model
    // with `{accepted: 3}` and no data made it answer a second time — and that
    // answer contradicted the one it had just given ("keine Umsatzergebnisse
    // gefunden" under a table full of them).
    if (pending.every((call) => this.clientTools.find(call.name)?.silent)) {
      this.state.update((s) => ({ ...s, running: false }));
      return;
    }

    this.startRun(messages, run.context, run.hop + 1);
  }

  private async executeClientTool(
    call: ChatToolCall
  ): Promise<{ content: string; error?: string }> {
    const tool = this.clientTools.find(call.name);
    if (!tool) {
      const message = `unknown client tool '${call.name}'`;
      this.state.update((s) => setToolCallStatus(s, call.id, 'error'));
      return { content: JSON.stringify({ error: message }), error: message };
    }
    try {
      const args = call.arguments ? JSON.parse(call.arguments) : {};
      const result = await tool.execute(args, this.injector);
      const content = JSON.stringify(result ?? null);
      this.state.update((s) => setToolCallStatus(s, call.id, 'done', content));
      return { content };
    } catch (e) {
      const message = String((e as Error)?.message ?? e);
      const content = JSON.stringify({ error: message });
      this.state.update((s) => setToolCallStatus(s, call.id, 'error', content));
      return { content, error: message };
    }
  }
}

function groupByParent(calls: ChatToolCall[]): Map<string | undefined, ChatToolCall[]> {
  const groups = new Map<string | undefined, ChatToolCall[]>();
  for (const call of calls) {
    const existing = groups.get(call.parentMessageId);
    if (existing) existing.push(call);
    else groups.set(call.parentMessageId, [call]);
  }
  return groups;
}
